import { DateRange } from 'app/shared/date-range-chooser/date-range-chooser.model';
import {
  CampaignScheduling,
  CampaignStatus,
  CampaignUrlSetting,
  OnsiteCampaign,
  OnsiteCampaignPausedReason,
  OnsiteCampaignType,
} from './campaign.model';
import {
  ComponentDescription,
  DeadlineFormat,
  DeadlineOccurrence,
  WidgetComponentSubType,
  WidgetComponentType,
  WidgetDailyStatisticsResponse,
  WidgetDailyStatisticsWrapper,
} from './widgets.model';
import TimeUtils from 'app/shared/utils/time-utils';
import { formatDate } from '@angular/common';
import { PageTitle } from 'app/shared/utils/page-title';

export default class CampaignHelpers {
  public static isCampaignFinished(campaign: OnsiteCampaign) {
    if (
      !campaign ||
      !campaign.status ||
      campaign.status === CampaignStatus.Draft ||
      !campaign.scheduling ||
      !campaign.scheduling.startDate ||
      !campaign.scheduling.endDate
    ) {
      return false;
    }
    if (campaign.status === CampaignStatus.Unpublished) {
      return true;
    } else {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const endDateConverted = new Date(Date.parse(campaign.scheduling.endDate));
      if (endDateConverted.getTime() < startOfToday.getTime()) {
        return true;
      }
    }
    return false;
  }

  public static isRatingComponent(componentDescription: ComponentDescription) {
    if (componentDescription.type !== WidgetComponentType.ButtonList) {
      return false;
    }
    switch (componentDescription.subType) {
      case WidgetComponentSubType.ButtonListRadio:
        return false;
      case WidgetComponentSubType.ButtonListChoices:
        return false;
      case WidgetComponentSubType.ButtonListCheckbox:
        return false;
      case WidgetComponentSubType.ButtonListSubscriptionCheckbox:
        return false;
      default:
        return true;
    }
  }

  public static isButtonListComponent(componentDescription: ComponentDescription) {
    if (componentDescription.type !== WidgetComponentType.ButtonList) {
      return false;
    }
    switch (componentDescription.subType) {
      case WidgetComponentSubType.ButtonListRadio:
      case WidgetComponentSubType.ButtonListChoices:
      case WidgetComponentSubType.ButtonListCheckbox:
      case WidgetComponentSubType.ButtonListSubscriptionCheckbox:
        return true;
    }
  }

  public static isCampaignScheduled(scheduling: CampaignScheduling): boolean {
    if (!scheduling) return false;
    const { startDate, endDate } = scheduling;
    if (!(startDate && endDate)) {
      return false;
    } else {
      const differenceDay = new Date().getTime() < new Date(Date.parse(startDate)).getTime();
      if (differenceDay) {
        return true;
      } else {
        return false;
      }
    }
  }

  // Statistics end today, so an open-ended test does not request centuries of empty future days.
  public static getAbTestStatisticsEndDate(scheduling: CampaignScheduling, now: Date = new Date()): string {
    const endTime = Date.parse(scheduling?.endDate);
    if (Number.isNaN(endTime) || endTime <= now.getTime()) {
      return scheduling?.endDate;
    }
    const startTime = Date.parse(scheduling?.startDate);
    if (!Number.isNaN(startTime) && startTime > now.getTime()) {
      return scheduling.startDate;
    }
    return now.toISOString();
  }

  public static isScheduledForCampaignOverview({ startDate, endDate }: CampaignScheduling) {
    if (!(startDate && endDate)) {
      return false;
    } else {
      const differenceDay = new Date().getTime() < new Date(Date.parse(startDate)).getTime();
      if (differenceDay) {
        return true;
      } else {
        return false;
      }
    }
  }

  public static formatDateForAnalytics(date: Date, daysIncrement = 0): string {
    if (!date) {
      return undefined;
    }
    const dateParsed = new Date(date);
    dateParsed.setDate(dateParsed.getDate() + daysIncrement);
    let month = '' + (dateParsed.getMonth() + 1);
    let day = '' + dateParsed.getDate();
    const year = dateParsed.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
    return [year, month, day].join('-');
  }

  public static calculateIncrease(valueAfter: number, valueBefore: number): number {
    if (valueBefore === 0) {
      return undefined;
    }
    if (valueAfter === 0) {
      return -100;
    }
    if (valueAfter >= valueBefore) {
      return ((valueAfter - valueBefore) / valueBefore) * 100;
    } else {
      return ((-1 * (valueBefore - valueAfter)) / valueBefore) * 100;
    }
  }

  public static emptyAllBackendTypeProperties(object: any): any {
    if (!object) {
      return object;
    }

    if (object['$type']) {
      object['$type'] = undefined;
    }

    const objectKeys = Object.keys(object);
    for (const key of objectKeys) {
      if (object[key] && object[key]['$type']) {
        object[key]['$type'] = undefined;
      }
    }
    return object;
  }

  public static trimWildcards(pagePath: string) {
    if (pagePath) {
      const endsWithWildcard = pagePath.lastIndexOf('*') === pagePath.length - 1;
      const startsWithWildcard = pagePath.indexOf('*') === 0;
      let pagePathTransform = pagePath
        .split('')
        .filter((item) => item.indexOf('*') === -1)
        .join('');
      if (pagePathTransform[0] !== '/' && !startsWithWildcard) {
        pagePathTransform = '/' + pagePathTransform;
      }
      return (startsWithWildcard ? '*' : '') + pagePathTransform + (endsWithWildcard ? '*' : '');
    }
  }

  public static trimWildcardsForPreview(pagePath: string) {
    if (pagePath) {
      let pagePathTransform = pagePath
        .split('')
        .filter((item) => item.indexOf('*') === -1)
        .join('');
      if (pagePathTransform[0] !== '/') {
        pagePathTransform = '/' + pagePathTransform;
      }
      return pagePathTransform;
    }
  }

  public static hexToRGB(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
  }

  public static getTransformStatisticsForWidgets(
    responses: Array<WidgetDailyStatisticsResponse>,
  ): Array<WidgetDailyStatisticsWrapper> {
    const result = [] as Array<WidgetDailyStatisticsWrapper>;

    const opensMap = {};
    const conversionsMap = {};
    const clickthroughMap = {};
    const couponsCopiedMap = {};
    const surveyResponsesMap = {};
    responses.forEach((response: WidgetDailyStatisticsResponse) => {
      response.data.items
        .find((item) => item.name === 'open')
        .items.forEach((dateItem: { date: string; count: number }) => {
          opensMap[dateItem.date] = opensMap[dateItem.date] ? opensMap[dateItem.date] + dateItem.count : dateItem.count;
        });

      response.data.items.find((item) => item.name === 'conversion').items = this.getCombineValue([
        ...response.data.items.find((item) => item.name === 'conversion').items,
        ...response.data.items.find((item) => item.name === 'conversionPartial').items,
      ]);

      response.data.items
        .find((item) => item.name === 'conversion')
        .items.forEach((dateItem: { date: string; count: number }) => {
          conversionsMap[dateItem.date] = conversionsMap[dateItem.date]
            ? conversionsMap[dateItem.date] + dateItem.count
            : dateItem.count;
        });
      response.data.items
        .find((item) => item.name === 'clickthrough')
        .items.forEach((dateItem: { date: string; count: number }) => {
          clickthroughMap[dateItem.date] = clickthroughMap[dateItem.date]
            ? clickthroughMap[dateItem.date] + dateItem.count
            : dateItem.count;
        });

      response.data.items
        .find((item) => item.name === 'couponsCopied')
        .items.forEach((dateItem: { date: string; count: number }) => {
          couponsCopiedMap[dateItem.date] = couponsCopiedMap[dateItem.date]
            ? couponsCopiedMap[dateItem.date] + dateItem.count
            : dateItem.count;
        });

      response.data.items.find((item) => item.name === 'surveyResponses').items = this.getCombineValue([
        ...response.data.items.find((item) => item.name === 'surveyResponses').items,
        ...response.data.items.find((item) => item.name === 'surveyResponsesPartial').items,
      ]);

      response.data.items
        .find((item) => item.name === 'surveyResponses')
        .items.forEach((dateItem: { date: string; count: number }) => {
          surveyResponsesMap[dateItem.date] = surveyResponsesMap[dateItem.date]
            ? surveyResponsesMap[dateItem.date] + dateItem.count
            : dateItem.count;
        });
    });

    result.push(this.parseDataItems('open', opensMap));
    result.push(this.parseDataItems('conversion', conversionsMap));
    result.push(this.parseDataItems('clickthrough', clickthroughMap));
    result.push(this.parseDataItems('couponsCopied', couponsCopiedMap));
    result.push(this.parseDataItems('surveyResponses', surveyResponsesMap));

    return result;
  }

  public static parseDataItems(name: string, dataMap: any) {
    const resultItem = {
      name: name,
      count: Object.keys(dataMap).length,
      items: [],
    } as WidgetDailyStatisticsWrapper;
    Object.keys(dataMap).forEach((openDate) => {
      resultItem.items.push({ date: openDate, count: dataMap[openDate] });
    });
    return resultItem;
  }

  static getCombineValue(combinedValue): any {
    const countSumByDate = {};
    combinedValue.forEach((item) => {
      const { date, count } = item;
      if (countSumByDate[date]) {
        countSumByDate[date] += count;
      } else {
        countSumByDate[date] = count;
      }
    });
    return Object.entries(countSumByDate).map(([date, count]) => ({
      date,
      count,
    }));
  }

  public static getCorrectPathFromCampaignSettingsForPreview(urlSettings: Array<CampaignUrlSetting>): string {
    if (!urlSettings || urlSettings.length === 0) {
      return '/';
    }
    const includeUrlSettings = urlSettings.filter((item: CampaignUrlSetting) => item.isPathTypeInclude === true);
    if (!includeUrlSettings) {
      return '/';
    }
    let firstPathWithoutWildcardIndex = -1;
    for (let i = 0; i < includeUrlSettings.length; i++) {
      if (firstPathWithoutWildcardIndex === -1 && includeUrlSettings[i].path.indexOf('*') === -1) {
        firstPathWithoutWildcardIndex = i;
      }
    }
    if (firstPathWithoutWildcardIndex !== -1) {
      return includeUrlSettings[firstPathWithoutWildcardIndex].path;
    }
    return '/';
  }

  public static checkDeadlineComponent(
    campaign: OnsiteCampaign,
    deadlineDate,
    showDeadlineWarning,
  ): {
    deadlineDate: string;
    showDeadlineWarning: boolean;
    specificDeadline: DeadlineFormat;
  } {
    showDeadlineWarning = false;
    let specificDeadline = null;
    const deadlineComponents: any = [];
    let campaignEndDate: string;
    if (campaign.scheduling.endDate) {
      campaignEndDate = campaign.scheduling.endDate;
    }

    campaign.widgets.forEach((widget) => {
      widget.states.forEach((state) => {
        state.containers.forEach((container) => {
          container.components.forEach((component) => {
            if (
              component.type === WidgetComponentType.Deadline &&
              component.subType === WidgetComponentSubType.DeadlineRegular
            ) {
              deadlineComponents.push({ component, widgetId: widget.id });
            }
          });
        });
      });
    });
    if (deadlineComponents.length) {
      if (
        (deadlineComponents.length >= 2 &&
          deadlineComponents.every(
            (item) => item.component.deadlineProperties.deadlineOccurrence === DeadlineOccurrence.EveryDay,
          )) ||
        (deadlineComponents.length >= 2 &&
          deadlineComponents.every(
            (item) => item.component.deadlineProperties.deadlineOccurrence === DeadlineOccurrence.OnlyOnce,
          ))
      ) {
        return { deadlineDate, showDeadlineWarning: true, specificDeadline };
      } else if (
        deadlineComponents.length &&
        deadlineComponents.some(
          (item) => item.component.deadlineProperties.deadlineOccurrence === DeadlineOccurrence.OnlyOnce,
        )
      ) {
        let allDeadlineComp = [];
        allDeadlineComp = deadlineComponents.filter((item) => {
          return item.component.deadlineProperties.deadlineOccurrence === DeadlineOccurrence.OnlyOnce;
        });
        if (allDeadlineComp.length === 1) {
          const componentDeadlineDate = allDeadlineComp[0].component.deadlineProperties.deadlineDate;
          deadlineDate = componentDeadlineDate;
          //TODO campaignEndDate === deadlineEndDate for all cases
          if (campaignEndDate && +new Date(campaignEndDate) === +new Date(componentDeadlineDate)) {
            //only for HOURS
            if (
              TimeUtils.getDateDiff(componentDeadlineDate, TimeUtils.getMoment(), 'minute') < 60 &&
              allDeadlineComp[0].component.deadlineProperties.format === DeadlineFormat.Hours
            ) {
              showDeadlineWarning = false;
              specificDeadline = DeadlineFormat.Hours;

              //only for DAYS
            } else if (
              TimeUtils.getDateDiff(componentDeadlineDate, campaignEndDate, 'hour') < 24 &&
              allDeadlineComp[0].component.deadlineProperties.format === DeadlineFormat.Days
            ) {
              showDeadlineWarning = false;
              specificDeadline = DeadlineFormat.Days;

              //only all rest
            } else {
              showDeadlineWarning = true;
            }
            //TODO campaignEndDate > deadlineEndDate DAYS
          } else if (
            campaignEndDate &&
            +new Date(campaignEndDate) > +new Date(componentDeadlineDate) &&
            allDeadlineComp[0].component.deadlineProperties.format === DeadlineFormat.Days
          ) {
            if (TimeUtils.getDateDiff(campaignEndDate, componentDeadlineDate, 'day') > 0) {
              if (TimeUtils.getDateDiff(componentDeadlineDate, campaignEndDate, 'minute') < 60) {
                specificDeadline = DeadlineFormat.Days;
              } else {
                specificDeadline = DeadlineFormat.HoursMinutesSeconds;
              }
              showDeadlineWarning = false;
            } else {
              showDeadlineWarning = true;
            }
            //TODO campaignEndDate > deadlineEndDate Hours
          } else if (
            campaignEndDate &&
            +new Date(campaignEndDate) > +new Date(componentDeadlineDate) &&
            allDeadlineComp[0].component.deadlineProperties.format === DeadlineFormat.Hours
          ) {
            if (TimeUtils.getDateDiff(campaignEndDate, componentDeadlineDate, 'day') > 0) {
              if (TimeUtils.getDateDiff(componentDeadlineDate, campaignEndDate, 'minute') < 60) {
                specificDeadline = DeadlineFormat.Days;
              } else {
                specificDeadline = DeadlineFormat.DaysHoursMinutes;
              }
              showDeadlineWarning = false;
            } else {
              showDeadlineWarning = true;
            }
            //TODO if no campaignEndDate for DAYS
          } else if (
            !campaignEndDate &&
            allDeadlineComp[0].component.deadlineProperties.format === DeadlineFormat.Days
          ) {
            showDeadlineWarning = false;
            specificDeadline = DeadlineFormat.Days;
            //TODO if no campaignEndDate for HOURS
          } else if (
            !campaignEndDate &&
            allDeadlineComp[0].component.deadlineProperties.format === DeadlineFormat.Hours
          ) {
            if (TimeUtils.getDateDiff(componentDeadlineDate, campaignEndDate, 'minute') < 60) {
              specificDeadline = DeadlineFormat.Days;
            } else {
              specificDeadline = DeadlineFormat.DaysHoursMinutes;
            }
            showDeadlineWarning = false;
          } else {
            //TODO for all other cases
            specificDeadline = allDeadlineComp[0].component.deadlineProperties.format;
            showDeadlineWarning = false;
          }
        } else {
          showDeadlineWarning = true;
        }
      } else {
        showDeadlineWarning = true;
      }
    } else {
      showDeadlineWarning = true;
    }
    return { deadlineDate, showDeadlineWarning, specificDeadline };
  }

  public static shouldDisableDeletion(campaign: OnsiteCampaign): boolean {
    if (campaign.referralCampaignGroup) {
      return true;
    }
    return false;
  }

  public static shouldDisableDeletionOnCampaigns(campaigns: OnsiteCampaign[]): boolean {
    const counts = new Map<any, number>();
    for (let i = 0; i < campaigns.length; i++) {
      if (campaigns[i].referralCampaignGroup) {
        const group = campaigns[i].referralCampaignGroup.id;
        counts.set(group, (counts.get(group) || 0) + 1);
      }
    }
    return campaigns.some((campaign) => counts.get(campaign.referralCampaignGroup?.id) === 1);
  }

  public static getInitialDatesForDatePicker(range?): DateRange {
    let startDate;
    let endDate;
    if (range === 'last7days') {
      startDate = CampaignHelpers.formatDateForAnalytics(TimeUtils.getSubtractDate(7, 'days').toDate());
      endDate = CampaignHelpers.formatDateForAnalytics(new Date());
    } else {
      const startDateDate = new Date();
      startDateDate.setMonth(startDateDate.getMonth() - 1);
      startDate = CampaignHelpers.formatDateForAnalytics(startDateDate);
      endDate = CampaignHelpers.formatDateForAnalytics(new Date());
    }
    return { startDate, endDate };
  }

  public static getCampaignStatusInfo(campaign, daysUntilReset, languageService) {
    let statusInfo = '';
    let locale = languageService.getLocaleFromCurrentLanguage().toString();
    let publishedDate = campaign.lastPublished ? formatDate(campaign.lastPublished, 'yyyy-MM-dd', locale) : '';
    let unPublishedDate = campaign.lastUnpublished ? formatDate(campaign.lastUnpublished, 'yyyy-MM-dd', locale) : '';
    let publishLabel = languageService.translateInstantly('campaign.status-Published-2');
    let lastPublishLabel = languageService.translateInstantly('campaign.filter.date.last-published');
    let lastUnPublishLabel = languageService.translateInstantly('campaign.filter.date.last-unpublished');
    let createdDate = formatDate(campaign.createdTime, 'yyyy-MM-dd', locale);
    let createdLabel = languageService.translateInstantly('audience.table.table-headers.created');
    let modifiedDate = formatDate(campaign.updatedTime, 'yyyy-MM-dd', locale);
    let modifiedLabel = languageService.translateInstantly('audience.table.table-headers.updated');
    let startDate = campaign.scheduling.startDate;
    let startDateLabel = languageService.translateInstantly('dateRangePicker.startDate');
    let endDate = campaign.scheduling.endDate;
    let endDateLabel = languageService.translateInstantly('dateRangePicker.endDate');
    let customDays = campaign.scheduling.customDays;
    let customHours = campaign.scheduling.customHours;
    let status = CampaignHelpers.setPausedCouponsCampaign(campaign)
      ? 'PausedCoupons'
      : CampaignHelpers.setPausedSurveyCampaign(campaign)
        ? 'PausedSurveys'
        : !campaign.isScheduled
          ? campaign.status
          : CampaignStatus.Scheduled;
    let statusLabel = languageService.translateInstantly('campaign.status-' + status).toUpperCase();
    let scheduledCampaignText = languageService.translateInstantly('campaign.scheduled-campaign-tooltip');
    if (status === CampaignStatus.Published) {
      statusInfo =
        `${statusLabel}` +
        '\n' +
        `${publishLabel}: ${publishedDate}` +
        '\n' +
        `${createdLabel}: ${createdDate}` +
        '\n' +
        `${modifiedLabel}: ${modifiedDate}`;
      if (campaign.scheduling.endDate) {
        statusInfo = statusInfo + '\n' + '\n' + scheduledCampaignText;
      }
    }
    if (status === CampaignStatus.Draft) {
      statusInfo =
        `${statusLabel}` + '\n' + `${createdLabel}: ${createdDate}` + '\n' + `${modifiedLabel}: ${modifiedDate}`;
    }
    if (status === CampaignStatus.Unpublished) {
      statusInfo =
        `${statusLabel}` +
        '\n' +
        `${createdLabel}: ${createdDate}` +
        '\n' +
        `${modifiedLabel}: ${modifiedDate}` +
        '\n' +
        `${lastPublishLabel}: ${publishedDate}` +
        '\n' +
        `${lastUnPublishLabel}: ${unPublishedDate}`;
    }
    if (status === CampaignStatus.Finished) {
      statusInfo =
        `${statusLabel}` +
        '\n' +
        `${createdLabel}: ${createdDate}` +
        '\n' +
        `${modifiedLabel}: ${modifiedDate}` +
        '\n' +
        `${lastPublishLabel}: ${publishedDate}` +
        '\n' +
        `${startDateLabel}:  ${formatDate(startDate, 'yyyy-MM-dd', locale)}` +
        '\n' +
        `${endDateLabel}: ${formatDate(endDate, 'yyyy-MM-dd', locale)}`;
    }
    if (status === CampaignStatus.Scheduled) {
      let alldays = languageService.translateInstantly('campaign.settings.scheduling.allDaysInWeek');
      let allHoursInDay = languageService.translateInstantly('campaign.settings.scheduling.allHoursInDay');
      statusInfo =
        `${statusLabel}` +
        '\n' +
        `${createdLabel}: ${createdDate}` +
        '\n' +
        `${startDateLabel}:  ${formatDate(startDate, 'yyyy-MM-dd:hh:mm', locale)}` +
        '\n' +
        `${endDateLabel}: ${formatDate(endDate, 'yyyy-MM-dd:hh:mm', locale)}` +
        '\n' +
        (customDays?.length || customHours?.length
          ? `${customDays?.length ? customDays.join() : alldays} ${!customHours?.length ? 'and' : ''} ${
              customHours?.length
                ? languageService.translateInstantly('campaign.settings.scheduling.betweenHours', {
                    startHours: customHours[0],
                    endHours: customHours[customHours.length - 1] + 1,
                  })
                : allHoursInDay
            }`
          : '');
    }
    if (status === 'PausedCoupons') {
      statusInfo =
        `${languageService.translateInstantly('campaign.status-Paused')}` +
        '\n' +
        languageService.translateInstantly('campaign.status-hover-paused-coupons') +
        '\n' +
        `${publishLabel}: ${publishedDate}` +
        '\n' +
        `${createdLabel}: ${createdDate}` +
        '\n' +
        `${modifiedLabel}: ${modifiedDate}`;
    }
    if (status === 'PausedSurveys') {
      statusInfo =
        `${languageService.translateInstantly('campaign.status-Paused')}` +
        '\n' +
        languageService.translateInstantly('campaign.status-Paused-Hover', { daysUntilReset: daysUntilReset }) +
        '\n' +
        `${publishLabel}: ${publishedDate}` +
        '\n' +
        `${createdLabel}: ${createdDate}` +
        '\n' +
        `${modifiedLabel}: ${modifiedDate}`;
    }
    return statusInfo;
  }

  public static setPausedSurveyCampaign(campaign) {
    return (
      campaign.pausedReason &&
      campaign.pausedReason === OnsiteCampaignPausedReason.SurveyResponsesOverLimit &&
      campaign.status === CampaignStatus.Published &&
      campaign.type !== OnsiteCampaignType.ABTesting
    );
  }

  public static setPausedCouponsCampaign(campaign) {
    return (
      campaign.pausedReason &&
      campaign.pausedReason === OnsiteCampaignPausedReason.CouponsUsageAtPauseLimit &&
      campaign.status === CampaignStatus.Published &&
      campaign.type !== OnsiteCampaignType.ABTesting
    );
  }

  public static toggleDatasetChart(dataChart, initialDataChart, chartMetrics, dataCheckBox) {
    const inputElement = dataCheckBox.event.target as HTMLInputElement;
    const value = inputElement.value;
    const checked = inputElement.checked;
    const itemsChartName = this.fetchItemsChartName(value);

    if (checked) {
      dataChart = dataChart.map((item) => {
        if (itemsChartName.includes(item.name)) {
          const initialData = initialDataChart.filter((item) => itemsChartName.includes(item.name))[0];
          return { ...item, count: initialData.count, items: initialData.items };
        }
        return item;
      });
    } else {
      dataChart = dataChart.map((item) => {
        if (itemsChartName.includes(item.name)) {
          return { ...item, count: null, items: [] };
        }
        return item;
      });
    }
    chartMetrics[dataCheckBox.index].selected = dataCheckBox.event.target.checked;
    return { dataChart, chartMetrics };
  }

  public static fetchItemsChartName(value): string[] {
    switch (value) {
      case 'view':
        return ['open'];
      case 'couponsCopied':
        return ['couponsCopied'];
      case 'conversion':
        return ['conversion', 'conversionPartial'];
      case 'clickthrough':
        return ['clickthrough'];
      case 'surveyResponses':
        return ['surveyResponses', 'surveyResponsesPartial'];
      case 'createdSubscribers':
        return ['CreatedSubscribers'];
      case 'duplicatesPrevented':
        return ['DuplicatesPrevented'];
      default:
        break;
    }
  }

  public static getUserPlatform() {
    const ua = navigator.userAgent || navigator.vendor;
    if (/windows/i.test(ua)) {
      return 'Windows';
    }
    if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) {
      return 'iOS';
    }
    return 'Other';
  }

  public static setCampaignPageContentKeys(currentPage, languageService) {
    switch (currentPage) {
      case PageTitle.Dashboard:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.campaign'),
          pageDescriptionKey: '',
          createButtonKey: 'widgets.campaigns.new-campaign-btn',
          activeCampaignsKey: 'campaign.list.active',
          inactiveCampaignsKey: 'campaign.list.inactive',
        };
        break;
      case PageTitle.Campaigns:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.campaign'),
          pageDescriptionKey: '',
          createButtonKey: 'widgets.campaigns.new-campaign-btn',
          activeCampaignsKey: 'campaign.list.active',
          inactiveCampaignsKey: 'campaign.list.inactive',
        };
        break;
      case PageTitle.AppExperiences:
        return {
          campaignDisplayType: languageService.translateInstantly('top-level-side-menu.app-experiences'),
          pageDescriptionKey: '',
          createButtonKey: 'app-experiences.landing.cta.create',
          activeCampaignsKey: 'campaign.list.active',
          inactiveCampaignsKey: 'campaign.list.inactive',
        };
        break;
      case PageTitle.Promotions:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.promotion'),
          pageDescriptionKey: 'campaign.promotions-descr',
          createButtonKey: 'widgets.campaigns.new-promotion-btn',
          activeCampaignsKey: 'campaign.list.promotions-active',
          inactiveCampaignsKey: 'campaign.list.promotions-inactive',
        };
        break;
      case PageTitle.Forms:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.form'),
          pageDescriptionKey: 'campaign.forms-descr',
          createButtonKey: 'widgets.campaigns.new-form-btn',
          activeCampaignsKey: 'campaign.list.forms-active',
          inactiveCampaignsKey: 'campaign.list.forms-inactive',
        };
        break;
      case PageTitle.Surveys:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.survey'),
          pageDescriptionKey: 'campaign.surveys-descr',
          createButtonKey: 'widgets.campaigns.new-survey-btn',
          activeCampaignsKey: 'campaign.list.surveys-active',
          inactiveCampaignsKey: 'campaign.list.surveys-inactive',
        };
        break;
      case PageTitle.Games:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.games'),
          pageDescriptionKey: 'campaign.games-descr',
          createButtonKey: 'widgets.campaigns.new-games-btn',
          activeCampaignsKey: 'campaign.list.games-active',
          inactiveCampaignsKey: 'campaign.list.games-inactive',
        };
        break;
      case PageTitle.NudgeTour:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.nudge-tour'),
          pageDescriptionKey: 'campaign.nudge-tour-descr',
          createButtonKey: 'widgets.campaigns.new-nudge-tour-btn',
          activeCampaignsKey: 'campaign.list.nudge-tour-active',
          inactiveCampaignsKey: 'campaign.list.nudge-tour-inactive',
        };
        break;
      case PageTitle.Quiz:
        return {
          campaignDisplayType: languageService.translateInstantly('generic.quiz'),
          pageDescriptionKey: 'campaign.quiz-descr',
          createButtonKey: 'widgets.campaigns.new-quiz-btn',
          activeCampaignsKey: 'campaign.list.quiz-active',
          inactiveCampaignsKey: 'campaign.list.quiz-inactive',
        };
        break;
      default:
        break;
    }
  }
}
